import { useEffect, useState } from 'react';
import { Formik, Form, ErrorMessage } from 'formik';
import { CountryDropdown, RegionDropdown } from 'react-country-region-selector';
import * as Yup from 'yup';
import FormikControl from '../FormikContainer/FormikControl';
import TextError from '../FormikContainer/TextError';
import PrimaryButton from '../PrimaryButton';
import Button from '../Button';
import { Dialog, DialogContent } from '../Dialog';

interface Props {
  onHide: () => void;
  show: boolean;
  onUpdate: (schoolInfo: {
    schoolName: string;
    country: string;
    schoolState: string;
    schoolCity: string;
    schoolZip: string;
  }) => void;
  schoolName: string;
  className: string;
}

interface FormValues {
  schoolName: string;
  country: string;
  schoolState: string;
  schoolCity: string;
  schoolZip: string;
}

const ValidationSchema = Yup.object().shape({
  schoolName: Yup.string().required('Please input a valid school name'),
  country: Yup.string().required('Required'),
  schoolState: Yup.string().required('Required'),
  schoolCity: Yup.string().required('Please input a valid city'),
  schoolZip: Yup.string().required('Please input a valid postal code'),
});

const SchoolEditorModal = (props: Props) => {
  const [schoolName, setSchoolName] = useState('');
  const [country, setCountry] = useState('United States');
  const [regionOrProvinceLabel, setRegionOrProvinceLabel] = useState('State');
  const [regionOrProvince, setRegionOrProvince] = useState('');
  const [city, setCity] = useState('');
  const [zipOrPostal, setZipOrPostal] = useState('');
  const [zipOrPostalCodeLabel, setZipOrPostalCodeLabel] = useState('Zip Code');
  const [zipOrPostalCodePlaceholder, setZipOrPostalCodePlaceholder] = useState('NNNNN');

  const initialValues: FormValues = {
    schoolName: '' || schoolName,
    country: '' || country,
    schoolState: regionOrProvince,
    schoolCity: city,
    schoolZip: zipOrPostal,
  };

  const onCountryChange = (value: string, setFieldValue: (field: string, value: string) => void) => {
    setCountry(value);
    setFieldValue('schoolState', '');
    setFieldValue('schoolCity', '');
    setFieldValue('schoolZip', '');

    if (value === 'United States') {
      setRegionOrProvinceLabel('State');
      setZipOrPostalCodeLabel('Zip Code');
      setZipOrPostalCodePlaceholder('NNNNN');
    } else if (value === 'Canada') {
      setRegionOrProvinceLabel('Province');
      setZipOrPostalCodeLabel('Postal Code');
      setZipOrPostalCodePlaceholder('XNX NXN');
    } else {
      setRegionOrProvinceLabel('Province');
      setZipOrPostalCodeLabel('Postal Code');
      setZipOrPostalCodePlaceholder('');
    }
  };

  const onRegionOrProvinceChange = (value: string) => {
    setRegionOrProvince(value);
  };

  const onCityChange = (value: string) => {
    setCity(value);
  };

  const onZipOrPostalChange = (value: string) => {
    setZipOrPostal(value);
  };

  const handleSubmit = (values: FormValues) => {
    const { schoolName, country, schoolState, schoolCity, schoolZip } = values;

    const SchoolInfo = {
      schoolName: schoolName,
      country: country,
      schoolState: schoolState,
      schoolCity: schoolCity,
      schoolZip: schoolZip,
    };

    props.onUpdate(SchoolInfo);
  };

  const handleOnHide = () => {
    props.onHide();
  };

  useEffect(() => {
    if (schoolName !== props.schoolName) {
      setSchoolName(props.schoolName);
    }
  }, [props.schoolName, schoolName]);

  return (
    <Dialog
      open={props.show}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          handleOnHide();
        }
      }}
    >
      <DialogContent
        className='w-[min(92vw,500px)] rounded-[1.5rem] border-0 bg-white p-0 text-[1.6rem] shadow-[0_14px_34px_rgba(0,0,0,0.2)]'
        overlayClassName='bg-black/50'
      >
        <div className='relative flex items-center justify-center rounded-t-[1.5rem] bg-[#706b77] px-[2rem] py-[1.4rem]'>
          <button
            type='button'
            aria-label='Close'
            onClick={handleOnHide}
            className='absolute right-[1.2rem] top-[1.2rem] flex h-[2.8rem] w-[2.8rem] items-center justify-center rounded-full border-0 bg-transparent p-0 text-[2.2rem] leading-none text-white outline-none transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/70'
          >
            ×
          </button>
          <h3 className="m-0 font-['GT-Walsheim-Pro-Bold'] text-[2rem] text-white">Edit School</h3>
        </div>
        <div className='px-[5rem] py-[3rem] max-[480px]:px-[1.6rem]'>
          <Formik initialValues={initialValues} validationSchema={ValidationSchema} onSubmit={handleSubmit}>
            {({ handleChange, setFieldValue }) => {
              return (
                <Form>
                  <FormikControl
                    control='input'
                    type='text'
                    label='School Name'
                    name='schoolName'
                    placeholder='School Name'
                  />
                  <label
                    className="control-label formLabel mb-[0.4rem] block font-['GT-Walsheim-Pro-Regular'] text-[#3f3f3f]"
                    style={{ paddingLeft: '5px' }}
                  >
                    Country
                  </label>
                  <CountryDropdown
                    id='countryPicker'
                    classes='form-control w-full appearance-none border border-[#ccc] bg-[url("/src/images/icons/chevron-down.svg")] bg-[length:2rem] bg-[right_1rem_center] bg-no-repeat px-[1rem] py-[0.5rem] font-["GT-Walsheim-Pro-Regular"] text-[#3f3f3f]'
                    defaultOptionLabel='Select a Country'
                    name='country'
                    value={country}
                    onChange={(value, event) => {
                      handleChange(event);
                      onCountryChange(value, setFieldValue);
                    }}
                  />
                  <div className='mb-[5px] min-h-[2rem]'>
                    <ErrorMessage name='country' component={TextError} />
                  </div>
                  <label
                    className="control-label formLabel mb-[0.4rem] block font-['GT-Walsheim-Pro-Regular'] text-[#3f3f3f]"
                    style={{ paddingLeft: '5px' }}
                  >
                    {regionOrProvinceLabel}
                  </label>
                  <RegionDropdown
                    id='regionPicker'
                    classes='form-control w-full appearance-none border border-[#ccc] bg-[url("/src/images/icons/chevron-down.svg")] bg-[length:2rem] bg-[right_1rem_center] bg-no-repeat px-[1rem] py-[0.5rem] font-["GT-Walsheim-Pro-Regular"] text-[#3f3f3f]'
                    defaultOptionLabel={`Select ${regionOrProvinceLabel}`}
                    country={country}
                    name='schoolState'
                    value={regionOrProvince}
                    onChange={(value, event) => {
                      handleChange(event);
                      onRegionOrProvinceChange(value);
                    }}
                  />
                  <div className='mb-[5px] min-h-[2rem]'>
                    <ErrorMessage name='schoolState' component={TextError} />
                  </div>
                  <FormikControl
                    control='input'
                    type='text'
                    name='schoolCity'
                    label='City'
                    placeholder='City'
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      handleChange(event);
                      onCityChange(event.target.value);
                    }}
                  />
                  <FormikControl
                    control='input'
                    type='text'
                    name='schoolZip'
                    label={zipOrPostalCodeLabel}
                    placeholder={zipOrPostalCodePlaceholder}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      handleChange(event);
                      onZipOrPostalChange(event.target.value);
                    }}
                  />
                  <div className='flex flex-col items-center justify-center gap-[2rem] [&>div:first-child]:w-full [&>div:first-child>button]:w-full'>
                    <PrimaryButton type='submit' small>
                      submit
                    </PrimaryButton>
                    <Button type='button' textBlue small onClick={handleOnHide}>
                      back
                    </Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SchoolEditorModal;
